import { useCallback, useLayoutEffect, useRef } from 'react';
import type { AllowedBoundingBox } from '../../../types/plan';

const globalToContinuous = (coord: number): number => (coord > 0 ? coord - 1 : coord);

const getGridCellStep = (container: HTMLDivElement): { x: number; y: number } => {
  const firstCell = container.querySelector<HTMLElement>('[data-plan-cell="true"]');
  if (!firstCell) return { x: 1, y: 1 };

  const row = firstCell.dataset.row;
  const col = firstCell.dataset.col;

  const sameRowCells = row
    ? container.querySelectorAll<HTMLElement>(`[data-plan-cell="true"][data-row="${row}"]`)
    : null;
  const sameColCells = col
    ? container.querySelectorAll<HTMLElement>(`[data-plan-cell="true"][data-col="${col}"]`)
    : null;

  const x = sameRowCells && sameRowCells.length > 1
    ? Math.abs(sameRowCells[1].offsetLeft - sameRowCells[0].offsetLeft)
    : firstCell.offsetWidth || 1;

  const y = sameColCells && sameColCells.length > 1
    ? Math.abs(sameColCells[1].offsetTop - sameColCells[0].offsetTop)
    : firstCell.offsetHeight || 1;

  return { x: x || 1, y: y || 1 };
};

export const useGridScrollMemory = (allowedBoundingBox: AllowedBoundingBox) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousAllowedBoxRef = useRef<AllowedBoundingBox | null>(null);
  const scrollMemoryRef = useRef<{ left: number; top: number }>({ left: 0, top: 0 });
  const isInitialCenteredRef = useRef(false);

  const handleContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    containerRef.current = node;
    if (isInitialCenteredRef.current) {
      node.scrollLeft = scrollMemoryRef.current.left;
      node.scrollTop = scrollMemoryRef.current.top;
    }
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    scrollMemoryRef.current = {
      left: e.currentTarget.scrollLeft,
      top: e.currentTarget.scrollTop,
    };
  }, []);

  useLayoutEffect(() => {
    const current = allowedBoundingBox;
    const previous = previousAllowedBoxRef.current;
    const container = containerRef.current;

    if (!container) {
      previousAllowedBoxRef.current = current;
      return;
    }

    // Первый рендер: однократно центрируем viewport по центру доступной баундари.
    // Далее больше НЕ центрируем автоматически — только сохраняем позицию пользователя.
    if (!isInitialCenteredRef.current) {
      const centeredLeft = Math.max(0, (container.scrollWidth - container.clientWidth) / 2);
      const centeredTop = Math.max(0, (container.scrollHeight - container.clientHeight) / 2);
      container.scrollLeft = centeredLeft;
      container.scrollTop = centeredTop;
      scrollMemoryRef.current = { left: centeredLeft, top: centeredTop };
      previousAllowedBoxRef.current = current;
      isInitialCenteredRef.current = true;
      return;
    }

    const previousMemory = scrollMemoryRef.current;
    let targetLeft = previousMemory.left;
    let targetTop = previousMemory.top;

    if (previous) {
      const deltaCols = globalToContinuous(previous.minCol) - globalToContinuous(current.minCol);
      const deltaRows = globalToContinuous(previous.minRow) - globalToContinuous(current.minRow);

      if (deltaCols !== 0 || deltaRows !== 0) {
        const step = getGridCellStep(container);
        targetLeft = Math.max(0, previousMemory.left + deltaCols * step.x);
        targetTop = Math.max(0, previousMemory.top + deltaRows * step.y);
      }
    }

    // Всегда восстанавливаем запомненный viewport после изменения баундари.
    // Это гасит остаточный «скачок» при расширении вправо/вниз (когда min не меняется).
    container.scrollLeft = targetLeft;
    container.scrollTop = targetTop;

    scrollMemoryRef.current = {
      left: targetLeft,
      top: targetTop,
    };
    previousAllowedBoxRef.current = current;
  }, [allowedBoundingBox]);

  return {
    handleContainerRef,
    handleScroll,
  };
};
